import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { select, Store } from '@ngrx/store';

import { Observable, of, throwError, EMPTY } from 'rxjs';
import { catchError, switchMap, retry, map, tap } from 'rxjs/operators';
import { UserToken } from '../component/auth/model/user.model';
import { getToken } from '../component/auth/state/auth.selectors';
import { AppState } from '../reducers';
import { MsgService } from '../component/common-components/msg/service/msg.service';

import { EndPoints, UrlProviderService } from './urlprovider.service';

export abstract class BaseRestApiService {
    constructor(protected http: HttpClient, protected urlProvider: UrlProviderService) {}

    protected processError(error: any): Observable<any> {
        if (error.status && error.status === 401) {
            return EMPTY;
        }
        console.log(error);
        return throwError(error);
        // return new ErrorObservable(error);+
    }

    protected get<T>(endpoint: EndPoints, extraParams?: any, queryParams?: any, retryCount: number = 0): Observable<T> {
        let params: any = {};

        if (extraParams) {
            params = { ...extraParams, ...params };
        }

        return this.http
            .get<T>(this.urlProvider.getUrl(endpoint, undefined, params, queryParams), {
                ...this.urlProvider.baseGetOptions(),
                params
            })
            .pipe(
                switchMap((res: any) => {
                    return of(res.body);
                }),
                retry(retryCount),
                catchError((error: any, caught: Observable<any>) => {
                    return this.processError(error);
                })
            );
    }

    protected put<T>(
        endpoint: EndPoints,
        guid: string,
        extraParams?: any,
        jsonParse: boolean = false,
        body: any = null,
        retryCount: number = 0
    ): Observable<T> {
        let params: any = {};

        if (jsonParse) {
            params.format = 'JSON';
        }

        if (extraParams) {
            params = { ...extraParams, ...params };
        }
        let urlStr = '';
        urlStr = this.urlProvider.getUrl(endpoint, undefined, params);
        return this.http
            .put<any>(urlStr, body, {
                ...this.urlProvider.baseGetOptions(),
                params
            })
            .pipe(
                catchError((error: any, caught: Observable<any>) => {
                    return this.processError(error);
                })
            );
    }

    protected post<T>(endpoint: EndPoints, jsonParse: boolean = false, extraParams?: any, body: any = null, retryCount: number = 0): Observable<T> {
        let params: any = {};

        if (jsonParse) {
            params.format = 'JSON';
        }

        if (extraParams) {
            params = { ...extraParams, ...params };
        }

        return this.http
            .post<any>(this.urlProvider.getUrl(endpoint, undefined, params), body, {
                ...this.urlProvider.baseGetOptions(),
                params
            })
            .pipe(
                switchMap((res: any) => {
                    return of(res.body);
                }),
                catchError((error: any, caught: Observable<any>) => {
                    return this.processError(error);
                })
            );
    }

    protected delete<T>(endpoint: EndPoints, jsonParse: boolean = false, extraParams?: any, queryParam?: any): Observable<T> {
        let params: any = {};

        if (jsonParse) {
            params.format = 'JSON';
        }

        if (extraParams) {
            params = { ...extraParams, ...params };
        }

        return this.http
            .delete<T>(this.urlProvider.getUrl(endpoint, undefined, params, queryParam), {
                ...this.urlProvider.baseGetOptions(),
                params
            })
            .pipe(
                switchMap((res: any) => {
                    return of(res.body);
                }),
                catchError((error: any, caught: Observable<any>) => {
                    return this.processError(error);
                })
            );
    }

    protected postSessionTracker<T>(
        endpoint: EndPoints,
        jsonParse: boolean = false,
        extraParams?: any,
        body: any = null,
        retryCount: number = 0
    ): Observable<T> {
        let params: any = {};

        if (jsonParse) {
            params.format = 'JSON';
        }

        if (extraParams) {
            params = { ...extraParams, ...params };
        }

        return this.http
            .post<any>(this.urlProvider.getUrlSessionTrack(endpoint, undefined, params), body, {
                ...this.urlProvider.baseGetOptions(),
                params
            })
            .pipe(
                switchMap((res: any) => {
                    return of(res.body);
                }),
                catchError((error: any, caught: Observable<any>) => {
                    return this.processError(error);
                })
            );
    }

    protected putSessionTracker<T>(
        endpoint: EndPoints,
        guid: string,
        extraParams?: any,
        jsonParse: boolean = false,
        body: any = null,
        retryCount: number = 0
    ): Observable<T> {
        let params: any = {};

        if (jsonParse) {
            params.format = 'JSON';
        }

        if (extraParams) {
            params = { ...extraParams, ...params };
        }
        let urlStr = '';
        urlStr = this.urlProvider.getUrlSessionTrack(endpoint, params);
        return this.http
            .put<any>(urlStr.substring(0, urlStr.length - 1), body, {
                ...this.urlProvider.baseGetOptions(),
                params
            })
            .pipe(
                catchError((error: any, caught: Observable<any>) => {
                    return this.processError(error);
                })
            );
    }

    protected getSessionTracker<T>(endpoint: EndPoints, extraParams?: any, queryParams?: any, retryCount: number = 0): Observable<T> {
        let params: any = {};

        if (extraParams) {
            params = { ...extraParams, ...params };
        }

        return this.http
            .get<T>(this.urlProvider.getUrlSessionTrack(endpoint, '', '', queryParams), {
                ...this.urlProvider.baseGetOptions(),
                params
            })
            .pipe(
                switchMap((res: any) => {
                    return of(res.body);
                }),
                retry(retryCount),
                catchError((error: any, caught: Observable<any>) => {
                    return this.processError(error);
                })
            );
    }

    protected getMSINCatalog<T>(endpoint: EndPoints, extraParams?: any, queryParams?: any, retryCount: number = 0): Observable<T> {
        let params: any = {};

        if (extraParams) {
            params = { ...extraParams, ...params };
        }

        return this.http
            .get<T>(this.urlProvider.getUrlMSINCatalog(endpoint, '', '', queryParams), {
                ...this.urlProvider.baseGetOptions(),
                params
            })
            .pipe(
                switchMap((res: any) => {
                    return of(res.body);
                }),
                retry(retryCount),
                catchError((error: any, caught: Observable<any>) => {
                    return this.processError(error);
                })
            );
    }

    protected putMSINCatalog<T>(
        endpoint: EndPoints,
        guid: string,
        extraParams?: any,
        jsonParse: boolean = false,
        body: any = null,
        retryCount: number = 0
    ): Observable<T> {
        let params: any = {};

        if (jsonParse) {
            params.format = 'JSON';
        }

        if (extraParams) {
            params = { ...extraParams, ...params };
        }
        let urlStr = '';
        urlStr = this.urlProvider.getUrlMSINCatalog(endpoint, params);
        return this.http
            .put<any>(urlStr.substring(0, urlStr.length - 1), body, {
                ...this.urlProvider.baseGetOptions(),
                params
            })
            .pipe(
                catchError((error: any, caught: Observable<any>) => {
                    return this.processError(error);
                })
            );
    }

    protected postMSINCatalog<T>(
        endpoint: EndPoints,
        jsonParse: boolean = false,
        extraParams?: any,
        body: any = null,
        queryParams?: any,
        retryCount: number = 0
    ): Observable<T> {
        let params: any = {};

        if (jsonParse) {
            params.format = 'JSON';
        }

        if (extraParams) {
            params = { ...extraParams, ...params };
        }

        return this.http
            .post<any>(this.urlProvider.getUrlMSINCatalog(endpoint, undefined, params, queryParams), body, {
                ...this.urlProvider.baseGetOptions(),
                params
            })
            .pipe(
                switchMap((res: any) => {
                    return of(res.body);
                }),
                catchError((error: any, caught: Observable<any>) => {
                    return this.processError(error);
                })
            );
    }

    protected deleteMSINCatalog<T>(endpoint: EndPoints, jsonParse: boolean = false, extraParams?: any, queryParam?: any): Observable<T> {
        let params: any = {};

        if (jsonParse) {
            params.format = 'JSON';
        }

        if (extraParams) {
            params = { ...extraParams, ...params };
        }

        return this.http
            .delete<T>(this.urlProvider.getUrlMSINCatalog(endpoint, undefined, params, queryParam), {
                ...this.urlProvider.baseGetOptions(),
                params
            })
            .pipe(
                switchMap((res: any) => {
                    return of(res.body);
                }),
                catchError((error: any, caught: Observable<any>) => {
                    return this.processError(error);
                })
            );
    }
}
